import BaseParser, { DisplayValue } from './BaseParser';
import _get from 'lodash/get';
import { filterIdentifiers } from './util';
import {
  BaseParserOptions,
  ExtensionOptions,
  IdentifierOptions,
} from './SharedTypes';

class FhirIdentifier extends BaseParser {
  private options: BaseParserOptions;

  /**
   * @param {string|string[]} options.identifier.system - the target system or list of target systems
   * @param {string|string[]} options.identifier.typeSystem - the target type system or list of target type systems
   * @param {string|string[]} options.identifier.typeCode - the target type code or list of target type codes
   * @param {string|string[]} options.extension.url - preferred extension url
   */
  constructor(options: BaseParserOptions = {}) {
    super();
    this.options = options;
  }

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getDisplayValue(fhirProperty: fhir.Identifier | fhir.Identifier[]) {
    if (fhirProperty === null || fhirProperty === undefined) return null;
    const identifiers = Array.isArray(fhirProperty)
      ? fhirProperty
      : [fhirProperty];

    const { system, typeSystem, typeCode } = _get<
      BaseParserOptions,
      'identifier',
      IdentifierOptions
    >(this.options, 'identifier', {});

    const { url: extensionUrl } = _get<
      BaseParserOptions,
      'extension',
      ExtensionOptions
    >(this.options, 'extension', {});

    if (!typeSystem && !typeCode && !extensionUrl) {
      return _get(identifiers, '[0].value', null) as string;
    }

    const targetIdentifiers = filterIdentifiers(
      identifiers,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      system,
      typeSystem,
      typeCode,
      extensionUrl,
    );
    if (!targetIdentifiers || !targetIdentifiers.length) return null;

    return _get<fhir.Identifier, 'value', string>(
      targetIdentifiers[0],
      'value',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
  }

  getDisplayValueList(
    fhirProperty: fhir.Identifier | fhir.Identifier[],
  ): DisplayValue[] {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (fhirProperty === null || fhirProperty === undefined) return null;
    const identifiers = Array.isArray(fhirProperty)
      ? fhirProperty
      : [fhirProperty];

    const { system, typeSystem, typeCode } = _get<
      BaseParserOptions,
      'identifier',
      IdentifierOptions
    >(this.options, 'identifier', {});

    const { url: extensionUrl } = _get<
      BaseParserOptions,
      'extension',
      ExtensionOptions
    >(this.options, 'extension', {});

    if (!typeSystem && !typeCode && !extensionUrl) {
      const values = identifiers
        .map(identifier => _get(identifier, 'value', null))
        .filter(Boolean);
      if (!values.length) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return null;
      }
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return values;
    }

    const targetIdentifiers = filterIdentifiers(
      identifiers,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      system,
      typeSystem,
      typeCode,
      extensionUrl,
    );
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!targetIdentifiers) return null;

    const values = targetIdentifiers
      .map(targetIdentifier =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        _get<fhir.Identifier, 'value', string>(targetIdentifier, 'value', null),
      )
      .filter(Boolean);
    if (!values.length) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }
    return values;
  }
}

export default FhirIdentifier;
